import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { Loader2, Globe, AlertTriangle, ChevronRight, CheckCircle2 } from "lucide-react";

export function PageAuditTable({ scanId, scanStatus, isInitialReportReady }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!scanId || (!isInitialReportReady && !["completed", "partial", "failed"].includes(scanStatus))) return;
    
    let active = true;
    const fetchPages = async () => {
      setLoading(true);
      try {
        // Fetch up to 50 pages for the table
        const res = await axios.get(`/adviser/scan/${scanId}/pages?skip=0&limit=50`);
        if (active) {
          setPages(res.data.pages || []);
          setError(null);
        }
      } catch (err) {
        if (active) setError(err.message || "Failed to load pages");
      } finally {
        if (active) setLoading(false);
      }
    };
    
    fetchPages();
    return () => { active = false; };
  }, [scanId, scanStatus, isInitialReportReady]);

  if (loading) {
    return (
      <Card className="p-12 flex flex-col items-center justify-center text-center">
        <Loader2 className="h-8 w-8 text-[#FEAB3D] animate-spin mb-4" />
        <p className="font-medium text-slate-700">Loading Page Audit Data…</p>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-8 text-center bg-red-50 border-red-200">
        <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-3" />
        <p className="font-medium text-red-700">Failed to load Page Audit</p>
        <p className="text-sm text-red-600 mt-1">{error}</p>
      </Card>
    );
  }

  if (!pages.length) {
    return (
      <Card className="p-8 text-center text-slate-500">
        No pages have been analyzed yet.
      </Card>
    );
  }

  const getScoreColor = (score) => {
    if (score >= 70) return "text-emerald-600 font-bold";
    if (score >= 40) return "text-amber-500 font-bold";
    return "text-red-500 font-bold";
  };

  return (
    <div className="space-y-4">
      {pages.map((page, idx) => {
        const urlObj = new URL(page.url, window.location.origin);
        const path = urlObj.pathname + urlObj.search;
        const pageScore = Number.isFinite(page.page_score) ? page.page_score : null;


        return (
          <Card key={idx} className="overflow-hidden">
            <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b bg-slate-50">
              <div className="flex items-center gap-3 overflow-hidden">
                <Globe className="h-5 w-5 text-slate-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 text-sm truncate" title={page.title || "No Title"}>
                    {page.title || "No Title"}
                  </p>
                  <a href={page.url} target="_blank" rel="noreferrer" className="text-xs text-[#FEAB3D] hover:underline truncate inline-block max-w-full">
                    {path}
                  </a>
                </div>
              </div>
              
              <div className="flex items-center gap-4 flex-shrink-0">
                {page.analysis?.generation_mode === "fallback" && <Badge variant="secondary">Partial analysis</Badge>}
                {page.ai_analysis_status === "failed" ? (
                  <Badge variant="destructive" className="bg-red-100 text-red-700 hover:bg-red-100 border-red-200">
                    Analysis unavailable
                  </Badge>
                ) : pageScore !== null ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Page Score</span>
                    <span className={`text-xl ${getScoreColor(pageScore)}`}>
                      {pageScore} <span className="text-sm text-slate-400 font-normal">/100</span>
                    </span>
                  </div>
                ) : (
                  <Badge variant="secondary" className="bg-slate-200 text-slate-600 hover:bg-slate-200">
                    {["completed", "partial", "failed"].includes(scanStatus) ? "Not analyzed" : "Awaiting analysis"}
                  </Badge>
                )}
              </div>
            </div>
            
            {pageScore !== null && <p className="px-4 py-2 text-sm text-slate-600">{page.issue_count} issues · {page.high_priority_count} high priority</p>}
            {pageScore !== null && page.score_components && (
              <details><summary className="px-4 py-3 cursor-pointer text-sm font-medium">Score components</summary>
              <div className="p-4 bg-white grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(page.score_components).map(([key, value]) => (
                  <div key={key} className="flex flex-col">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-slate-500 uppercase truncate pr-2">
                        {key.replace(/_/g, " ")}
                      </span>
                      <span className={`text-xs font-bold ${getScoreColor(value / (page.score_component_maxima?.[key] || 100) * 100)}`}>{value} / {page.score_component_maxima?.[key] || 100}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5">
                      <div className={`h-1.5 rounded-full ${value / (page.score_component_maxima?.[key] || 100) >= .7 ? 'bg-emerald-500' : value / (page.score_component_maxima?.[key] || 100) >= .4 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${value / (page.score_component_maxima?.[key] || 100) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div></details>
            )}
          </Card>
        );
      })}
    </div>
  );
}
